import { Desk, DeskBookingStatus, loadDesks, useAppDispatch, withAsyncThunkErrorHandling } from "@lib/store";
import { MapSelectorCard_internal } from "./MapSelectorCard_internal";
import { useEffect, useState } from "react";
import { isDeskReservation, Workspace } from "@lib/store/src/data/timeslots/apiTypes";
import { MapViewUser } from "@lib/utils";
import { SelectedDeskUser } from "../DeskSelectionModalPage/types";
import { ToastData } from "./types";
import { useTranslation } from "react-i18next";
import { Attendee } from "@lib/store/src/data/timeslots/selectors/getAttendeeOverview";
import { useUserProfilePics } from "@hooks";
import { ModalCell, UserCell } from "@molecules";
import { FlexCol, H4 } from "@quarks";

type MapSelectorProps = {
  attendees: Array<Attendee>;
};

// TODO: should cache this in the store probably... this only supports switching area without unmounting
const useDesks = (areaId: string) => {
  const dispatch = useAppDispatch();

  const [desksByAreaId, setDesksByAreaId] = useState<Record<string, Array<Desk> | null | undefined>>({});

  useEffect(
    function loadDesksAndAddToCache() {
      if (!areaId) return;
      if (areaId in desksByAreaId) return;

      // Null indicates load
      setDesksByAreaId(prev => ({ ...prev, [areaId]: null }));

      dispatch(withAsyncThunkErrorHandling(() => loadDesks(areaId))).then(result => {
        if (result.success) {
          setDesksByAreaId(prev => ({ ...prev, [areaId]: result.result }));
        }
      });
    },
    [areaId, desksByAreaId, dispatch],
  );

  return areaId ? desksByAreaId[areaId] : undefined;
};

export const MapSelectorCard = ({ attendees }: MapSelectorProps) => {
  const { t } = useTranslation();

  // Anchor on the user's workspace
  const reference = attendees.find(a => a.isCurrentUser) ?? attendees.find(a => a.isOrganizer) ?? attendees[0];
  const referenceWorkspace: Workspace | undefined = reference?.reservation.workspace;

  const buildingId = referenceWorkspace?.buildingId ?? "";
  const floorId = referenceWorkspace && "floorId" in referenceWorkspace ? referenceWorkspace.floorId : "";
  const areaId = referenceWorkspace && "areaId" in referenceWorkspace ? referenceWorkspace.areaId : "";

  const isOnMap = ({ reservation }: Attendee) =>
    "areaId" in reservation.workspace && reservation.workspace.areaId === areaId;

  const attendeesOnMap = attendees.filter(isOnMap);
  const attendeesElsewhere = attendees.filter(attendee => !isOnMap(attendee));

  const desks = useDesks(areaId);

  const connectionProfileImages = useUserProfilePics(
    attendees.map(a => ({
      userId: a.owner.id,
      imageHash: a.owner.imageHash ?? "",
    })),
  );

  const deskIds = Array<string>();
  const connectionsByDeskId = new Map<string, MapViewUser>();
  const deskIdsBookedByOthers: DeskBookingStatus = {};
  const toasterData: Record<string, ToastData> = {};

  for (const { reservation, owner, isCurrentUser } of attendeesOnMap) {
    if (!isDeskReservation(reservation)) continue;

    const deskId = reservation.workspace.deskId;
    deskIds.push(deskId);
    connectionsByDeskId.set(deskId, {
      color: owner.email,
      initials: owner.initials,
      picture: owner.imageHash ?? "",
    });

    toasterData[reservation.workspace.deskId] = {
      displayName: isCurrentUser ? t("You") : owner.name,
      user: owner,
      deskName: reservation.workspace.deskName,
    };
  }

  for (const deskId of deskIds) {
    if (!connectionsByDeskId.has(deskId)) {
      // TODO: fetch desk availability instead of assuming true
      deskIdsBookedByOthers[deskId] = true;
    }
  }

  const users = attendeesOnMap.map((m): SelectedDeskUser => {
    return {
      email: m.owner.email,
      id: m.reservation.owner.id,
      initials: m.owner.initials,
      imageHash: m.owner.imageHash ?? "",
      imageUrl: connectionProfileImages[m.owner.id],
    };
  });

  if (!areaId || !floorId || !buildingId) return null;

  return (
    <FlexCol
      gap={16}
      height="100%">
      <MapSelectorCard_internal
        buildingId={buildingId}
        floorId={floorId}
        areaId={areaId}
        deskIds={deskIds.length ? deskIds : undefined}
        connectionsByDeskId={connectionsByDeskId}
        deskIdsBookedByOthers={deskIdsBookedByOthers}
        desks={desks ?? []}
        loading={!desks}
        users={users}
        toasterData={toasterData}
      />

      {attendeesElsewhere.length > 0 && (
        <ModalCell>
          <FlexCol gap={8}>
            <H4 as="h3">{t("workplace:BookedInAnotherWorkspace")}</H4>

            {attendeesElsewhere.map(attendee => (
              <UserCell
                key={attendee.owner.id}
                separator={false}
                padding="small"
                user={
                  attendee.isCurrentUser ? { ...attendee.owner, displayName: t("translation:You") } : attendee.owner
                }
                subtitle={locationLabel(attendee.reservation.workspace)}
              />
            ))}
          </FlexCol>
        </ModalCell>
      )}
    </FlexCol>
  );
};

const locationLabel = (workspace: Workspace): string => {
  switch (workspace.nodeType) {
    case "Desk":
      return `${workspace.deskName}, ${workspace.areaName}`;
    case "Area":
      return workspace.areaName;
    case "Floor":
      return workspace.floorName;
    case "Building":
      return workspace.buildingName;
  }
};
